import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Reusable two-dropdown-plus-answer-fields block for picking security
 * questions. Used in both signup (Auth.js) and Settings → Security.
 *
 * Props:
 *   value:    { q1, a1, q2, a2 } current state
 *   onChange: (next) => void
 *   showAnswers: boolean (default true) — hide answer inputs on display-only screens
 *   testIdPrefix: optional string prepended to data-testid attrs for uniqueness
 */
export default function SecurityQuestionsFields({
  value,
  onChange,
  testIdPrefix = "sq",
}) {
  const [pool, setPool] = useState([]);

  useEffect(() => {
    api
      .get("/auth/security-questions/pool")
      .then((r) => setPool(r.data?.questions || []))
      .catch(() => setPool([]));
  }, []);

  const set = (patch) => onChange({ ...value, ...patch });
  // Prevent choosing the same question twice.
  const q1Pool = pool.filter((q) => q !== value.q2);
  const q2Pool = pool.filter((q) => q !== value.q1);

  const row = (idx, qKey, aKey, options) => (
    <div className="space-y-1.5">
      <label
        className="text-sm font-medium block"
        style={{ color: "var(--text-secondary)" }}
      >
        Security question {idx}
      </label>
      <select
        className="pm-input"
        value={value[qKey] || ""}
        onChange={(e) => set({ [qKey]: e.target.value })}
        data-testid={`${testIdPrefix}-q${idx}-select`}
      >
        <option value="" disabled>
          Pick a question…
        </option>
        {options.map((q) => (
          <option key={q} value={q}>
            {q}
          </option>
        ))}
      </select>
      <input
        className="pm-input"
        type="text"
        placeholder="Your answer"
        value={value[aKey] || ""}
        onChange={(e) => set({ [aKey]: e.target.value })}
        autoComplete="off"
        data-testid={`${testIdPrefix}-a${idx}-input`}
      />
    </div>
  );

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-fields`}>
      {row(1, "q1", "a1", q1Pool)}
      {row(2, "q2", "a2", q2Pool)}
      <p
        className="text-xs"
        style={{ color: "var(--text-muted)" }}
      >
        Answers are case- and space-insensitive. Pick things you'll always
        remember — these let you reset your password if you forget it.
      </p>
    </div>
  );
}
